import clsx from 'clsx'
import { usePostHog } from 'posthog-js/react'
import { useMemo, useRef, useState } from 'react'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import type { ApplicantRenderProps } from '~/components/Scopes/Tables/Applicant'
import { themeClass, vars } from '~/styles/theme.css'
import { formatDateShort } from '~/utils/date'
import { captureApplicantAvailability } from '../../analytics'
import { useApplicantAvailability } from '../../queries/applicantAvailabilities'
import type { OccupationData } from '../../types'
import {
  availabilityCurrentOccupation,
  availabilityDotBtn,
  availabilityMain,
  availabilityTick,
  availabilityTickBig
} from './Availability.css'
import { AvailabilityDropdown } from './AvailabilityDropdown'

const tryFormatDate = (value: string) => {
  if (!/^\d{4}-/.test(value)) return value
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : formatDateShort(d)
}

const occupationColors = {
  available: vars.color.success,
  unavailable: vars.color.danger,
  unavailable_in_future: '#b883ff',
  available_in_future: vars.color.warning,
  neutral: vars.color.medium
}

const computeOccupationDisplay = (
  occupation: OccupationData,
  lastAvailabilityDate?: string
): { label: string; color: string } => {
  if (occupation.status === 'available') {
    return {
      label:
        occupation.currentlyAvailable && lastAvailabilityDate
          ? `Disponible depuis le ${tryFormatDate(lastAvailabilityDate)}`
          : 'Disponible',
      color: occupationColors.available
    }
  }

  if (occupation.status === 'unavailable') {
    return {
      label:
        occupation.date !== undefined && occupation.date !== ''
          ? `Indisponible jusqu'au ${tryFormatDate(occupation.date)}`
          : 'Indisponible',
      color: occupationColors.unavailable
    }
  }

  if (
    occupation.status === 'unavailable_in_future' &&
    occupation.startDate !== undefined &&
    occupation.date === undefined
  ) {
    return {
      label: `Bientôt indisponible le ${tryFormatDate(occupation.startDate)}`,
      color: occupationColors.unavailable_in_future
    }
  }

  if (
    occupation.status === 'unavailable_in_future' &&
    occupation.startDate !== undefined &&
    occupation.date !== undefined
  ) {
    return {
      label: `Bientôt indisponible du ${tryFormatDate(occupation.startDate)} au ${tryFormatDate(occupation.date)}`,
      color: occupationColors.unavailable_in_future
    }
  }

  if (
    occupation.status === 'available_in_future' &&
    occupation.date !== undefined
  ) {
    return {
      label: `Disponible le ${tryFormatDate(occupation.date)}`,
      color: occupationColors.available_in_future
    }
  }

  return { label: 'Disponibilité inconnue', color: occupationColors.neutral }
}

type AvailabilityProps = {
  applicantId: number
  applicantData?: ApplicantRenderProps
  variant?: 'dot' | 'embeddedDot' | 'embeddedDotBig'
  onUpdateAvailability?: (applicantId: number) => void
  source?: string
}

export const Availability = ({
  applicantId,
  applicantData,
  variant,
  onUpdateAvailability,
  source = 'unknown'
}: AvailabilityProps) => {
  const posthog = usePostHog()
  const { data: fetchedAvailability } = useApplicantAvailability({
    applicantId: applicantId
  })

  const occupation: OccupationData | undefined = fetchedAvailability
    ? {
        status: fetchedAvailability.availability.status,
        date: fetchedAvailability.availability.date || undefined,
        startDate: fetchedAvailability.availability.startDate,
        currentlyAvailable: fetchedAvailability.availability.currentlyAvailable
      }
    : undefined

  const lastAvailabilityDate: string | undefined =
    fetchedAvailability?.availability.currentlyAvailable === true
      ? (fetchedAvailability.availability.date ?? undefined)
      : undefined

  const { label: currentOccupation, color: currentOccupationColor } = useMemo(
    () =>
      occupation
        ? computeOccupationDisplay(occupation, lastAvailabilityDate)
        : { label: 'Disponibilité inconnue', color: occupationColors.neutral },
    [occupation, lastAvailabilityDate]
  )

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDropdownOpen) {
      captureApplicantAvailability(posthog, 'open', { applicantId, source })
    }
    setIsDropdownOpen((prev) => !prev)
  }

  if (!occupation) return null

  const tooltipOnly =
    variant === 'dot' ||
    variant === 'embeddedDot' ||
    variant === 'embeddedDotBig'
  const embeddedDot = variant === 'embeddedDot' || variant === 'embeddedDotBig'

  return (
    <div className={themeClass}>
      <div className={availabilityMain} ref={dropdownRef}>
        {!tooltipOnly && (
          <span className={availabilityCurrentOccupation}>
            {currentOccupation}
          </span>
        )}
        <Tooltip asChild text={tooltipOnly ? currentOccupation : null}>
          <button
            ref={buttonRef}
            type="button"
            className={clsx(
              embeddedDot ? availabilityTick : availabilityDotBtn,
              embeddedDot && variant === 'embeddedDotBig'
                ? availabilityTickBig
                : null
            )}
            onClick={handleIconClick}
            aria-label="Indiquer le candidat comme disponible"
            style={
              embeddedDot
                ? { background: currentOccupationColor }
                : {
                    background: currentOccupationColor,
                    borderColor: currentOccupationColor
                  }
            }
          />
        </Tooltip>
      </div>

      <AvailabilityDropdown
        applicantId={applicantId}
        isValidPhoneMobile={
          applicantData?.data.is_valid_phone_mobile ?? undefined
        }
        acceptsMessaging={applicantData?.data.accepts_messaging ?? undefined}
        isOpen={isDropdownOpen}
        onClose={() => setIsDropdownOpen(false)}
        dropdownRef={embeddedDot ? buttonRef : dropdownRef}
        placement={embeddedDot ? 'bottom-end' : 'bottom-start'}
        onUpdateAvailability={onUpdateAvailability}
        source={source}
      />
    </div>
  )
}
